import { useS3Download } from '@doltech/core/lib/hooks/useS3Download';
import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { ScriptTypeEnum } from '@doltech/utils/lib/constants';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import styled from 'styled-components';

import { ReadingTestLayout } from '../../student-do-test/layouts/ReadingTestLayout';
import { TestContent } from '../components/TestContent';
import { useTestResultStore } from '../model/test-result.hook';

const TestResultMonologScript = dynamic<any>(
  () =>
    import('@doltech/ui/lib/figma/ScriptSetup/TestResultMonologScript').then(
      (mod: any) => mod.default
    ),
  {
    ssr: false,
  }
);
const TestResultConversationScript = dynamic<any>(
  () =>
    import('@doltech/ui/lib/figma/ScriptSetup/TestResultConversationScript').then(
      (mod: any) => mod.default
    ),
  {
    ssr: false,
  }
);

const MonologScriptWrapper = styled.div`
  .audio-player-container {
    bottom: 120px;
  }
  .subtitle-container {
    overflow-y: initial;
    width: 100%;
    max-height: initial;
  }
`;

export const ListeningTestResult = observer(() => {
  const store = useTestResultStore();
  if (!store.initialed) return null;
  const currentSection = store?.testSections?.find(
    (_section, sectionIndex) => sectionIndex === store.currentSectionIndex
  );
  const currentQuestion = store?.currentQuestion;
  if (isNil(currentSection)) return null;

  const { correction } = currentQuestion;
  const audioFile = useS3Download(currentSection.script.audio.path);
  const peaks = JSON.parse(currentSection.script.waveInfo);
  let { explanation } = currentQuestion.correction;
  if (typeof explanation === 'string') {
    explanation = JSON.parse(currentQuestion.correction.explanation);
  }

  const { audioManager } = store.getCurrentTestSection();

  const renderLeftContent = React.useCallback(() => {
    switch (currentSection.script.type) {
      case ScriptTypeEnum.MONOLOG: {
        return (
          <MonologScriptWrapper>
            <TestResultMonologScript
              key={currentQuestion.uuid + store?.currentQuestionIndex}
              subtitle={currentSection.script.subtitle}
              explanation={explanation}
              audioUrl={audioFile.data?.data?.downloadResourceUrl}
              peaks={peaks}
              progressInSeconds={audioManager.progressInSeconds}
            />
          </MonologScriptWrapper>
        );
      }
      case ScriptTypeEnum.CONVERSATION: {
        return (
          <TestResultConversationScript
            key={currentQuestion.uuid + store?.currentQuestionIndex}
            audioUrl={audioFile.data?.data?.downloadResourceUrl}
            characters={currentSection.script.characters}
            subtitle={currentSection.script.subtitle}
            explanation={explanation}
            peaks={peaks}
            progressInSeconds={audioManager.progressInSeconds}
          />
        );
      }
      default:
        return null;
    }
  }, [
    audioFile.data?.data?.downloadResourceUrl,
    audioManager.progressInSeconds,
    currentQuestion.uuid,
    currentSection.script.characters,
    currentSection.script.subtitle,
    currentSection.script.type,
    explanation,
    peaks,
    store?.currentQuestionIndex,
  ]);

  return (
    <ReadingTestLayout id="test-result-container">
      <div className="content-wrapper">
        <div id="sidebar-left" className="no-overflow">
          <div className="content">{renderLeftContent()}</div>
        </div>

        <TestContent
          id="body-container"
          explanationAction={
            <Button.Primary
              variant="white"
              className="listen-button"
              iconLeft={<IconOtherPlay />}
              onClick={() => {
                audioManager.playRange(
                  correction.explanation.startTimeInSeconds,
                  correction.explanation.endTimeInSeconds
                );
              }}
            >
              Listen from here
            </Button.Primary>
          }
        />
      </div>
    </ReadingTestLayout>
  );
});
